import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildDbUrl } from "@csf/csfloat";
import type { ItemFilter, ItemKind, SkinCategory } from "@csf/shared";
import { useCreateItem } from "../api/items.js";
import {
  type KeychainCatalogItem,
  type SkinCatalogItem,
  type StickerCatalogItem,
} from "../api/catalog.js";
import { CatalogSearch } from "../components/CatalogSearch.js";
import { KindTabs } from "../components/KindTabs.js";
import { ErrorState } from "../components/Loading.js";
import { CronPicker } from "../components/CronPicker.js";
import {
  WEAR_OPTIONS,
  WearPicker,
  effectiveRange,
  type WearOption,
} from "../components/WearPicker.js";

export function AddPage() {
  const navigate = useNavigate();
  const create = useCreateItem();

  const [kind, setKind] = useState<ItemKind>("skin");
  const [displayName, setDisplayName] = useState("");
  const [skin, setSkin] = useState<SkinCatalogItem | null>(null);
  const [sticker, setSticker] = useState<StickerCatalogItem | null>(null);
  const [keychain, setKeychain] = useState<KeychainCatalogItem | null>(null);
  // Для скинов: качество вместо ручных min/max.
  const [wear, setWear] = useState<WearOption>(WEAR_OPTIONS[0]!); // "any"
  // null = «все варианты» (CSFloat не получает category=). Иначе normal/stattrak/souvenir.
  const [category, setCategory] = useState<SkinCategory | null>(null);
  // Расписание: null = по умолчанию (глобальное).
  const [scheduleCron, setScheduleCron] = useState<string | null>(null);

  const filter = buildFilter(kind, { skin, sticker, keychain, wear, category });
  const previewUrl = filter ? buildDbUrl(filter) : null;
  const ready = filter !== null && displayName.trim().length > 0;

  const handleSubmit = () => {
    if (!filter || !ready) return;
    create.mutate(
      {
        displayName: displayName.trim(),
        filter,
        // Передаём scheduleCron только если задан явно. Пустой/null → API
        // не выставит scheduleCron, scheduler будет использовать глобальный default.
        ...(scheduleCron ? { scheduleCron } : {}),
      },
      {
        onSuccess: ({ item }) => navigate(`/items/${item.id}`),
      },
    );
  };

  const onKindChange = (v: ItemKind | "all") => {
    if (v === "all") return;
    setKind(v);
    setSkin(null);
    setSticker(null);
    setKeychain(null);
    setDisplayName("");
    setWear(WEAR_OPTIONS[0]!);
    setCategory(null);
  };

  return (
    <div className="space-y-3 p-3">
      <h1 className="text-lg font-bold">Добавить предмет</h1>

      <KindTabs value={kind} onChange={onKindChange} includeAll={false} />

      <Field label="Поиск по каталогу">
        {kind === "skin" && (
          <CatalogSearch
            kind="skin"
            placeholder="AK-47 Redline..."
            onPick={(it) => {
              setSkin(it);
              setDisplayName(it.displayName);
              setWear(WEAR_OPTIONS[0]!); // сбрасываем на «Любое» при смене скина
              setCategory(null);
            }}
          />
        )}
        {kind === "sticker" && (
          <CatalogSearch
            kind="sticker"
            placeholder="iBUYPOWER Holo..."
            onPick={(it) => {
              setSticker(it);
              setDisplayName(it.displayName);
            }}
          />
        )}
        {kind === "keychain" && (
          <CatalogSearch
            kind="keychain"
            placeholder="Hot Howl..."
            onPick={(it) => {
              setKeychain(it);
              setDisplayName(it.displayName);
            }}
          />
        )}
      </Field>

      {(skin || sticker || keychain) && (
        <SelectedCard kind={kind} skin={skin} sticker={sticker} keychain={keychain} />
      )}

      <Field label="Название (для UI)">
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Заполняется автоматически"
          className="w-full rounded-xl bg-bg-secondary px-4 py-2 outline-none placeholder:text-hint"
        />
      </Field>

      {kind === "skin" && skin && (
        <Field label="Качество">
          <WearPicker
            skinMinFloat={skin.minFloat}
            skinMaxFloat={skin.maxFloat}
            selectedKey={wear.key}
            onChange={setWear}
          />
        </Field>
      )}

      {kind === "skin" && skin && (
        <Field label="Вариант">
          <CategoryPicker value={category} onChange={setCategory} />
        </Field>
      )}

      <Field label="Расписание парсинга">
        <CronPicker value={scheduleCron} onChange={setScheduleCron} />
      </Field>

      {previewUrl && (
        <div className="rounded-xl bg-bg-secondary p-3">
          <div className="mb-1 text-xs uppercase tracking-wide text-hint">
            Будет парсить
          </div>
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className="break-all text-xs text-link"
          >
            {previewUrl}
          </a>
        </div>
      )}

      {create.isError && <ErrorState error={create.error} />}

      <button
        type="button"
        disabled={!ready || create.isPending}
        onClick={handleSubmit}
        className="w-full rounded-xl bg-button px-4 py-3 font-medium text-button-text disabled:opacity-50"
      >
        {create.isPending ? "..." : "Добавить"}
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs uppercase tracking-wide text-hint">{label}</div>
      {children}
    </label>
  );
}

function SelectedCard(props: {
  kind: ItemKind;
  skin: SkinCatalogItem | null;
  sticker: StickerCatalogItem | null;
  keychain: KeychainCatalogItem | null;
}) {
  let icon: string | null = null;
  let color: string | null = null;
  let meta = "";

  if (props.kind === "skin" && props.skin) {
    icon = props.skin.iconUrl;
    color = props.skin.rarityColor;
    meta = `defIndex=${props.skin.defIndex}, paintIndex=${props.skin.paintIndex}, float ${props.skin.minFloat}…${props.skin.maxFloat}`;
  } else if (props.kind === "sticker" && props.sticker) {
    icon = props.sticker.iconUrl;
    color = props.sticker.rarityColor;
    meta = `stickerId=${props.sticker.stickerId}`;
  } else if (props.kind === "keychain" && props.keychain) {
    icon = props.keychain.iconUrl;
    color = props.keychain.rarityColor;
    meta = `keychainId=${props.keychain.keychainId}`;
  } else {
    return null;
  }

  return (
    <div className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3">
      {icon && <img src={icon} alt="" className="h-12 w-12 rounded object-contain" />}
      <div className="min-w-0 flex-1">
        <div className="text-xs text-hint">{meta}</div>
      </div>
      <div
        className="h-3 w-3 flex-shrink-0 rounded-full"
        style={color ? { backgroundColor: `#${color}` } : { backgroundColor: "transparent" }}
      />
    </div>
  );
}

interface FilterParts {
  skin: SkinCatalogItem | null;
  sticker: StickerCatalogItem | null;
  keychain: KeychainCatalogItem | null;
  wear: WearOption;
  category: SkinCategory | null;
}

function buildFilter(kind: ItemKind, p: FilterParts): ItemFilter | null {
  if (kind === "skin") {
    if (!p.skin) return null;
    const { min, max } = effectiveRange(p.skin.minFloat, p.skin.maxFloat, p.wear);
    if (min > max) return null;
    return {
      kind: "skin",
      name: p.skin.displayName,
      defIndex: p.skin.defIndex,
      paintIndex: p.skin.paintIndex,
      min,
      max,
      ...(p.category ? { category: p.category } : {}),
    };
  }
  if (kind === "sticker") {
    if (!p.sticker) return null;
    return { kind: "sticker", stickerId: p.sticker.stickerId };
  }
  if (!p.keychain) return null;
  return { kind: "keychain", keychainId: p.keychain.keychainId };
}

const CATEGORY_OPTIONS: Array<{ key: SkinCategory | null; label: string }> = [
  { key: null, label: "Все" },
  { key: "normal", label: "Normal" },
  { key: "stattrak", label: "StatTrak" },
  { key: "souvenir", label: "Souvenir" },
];

function CategoryPicker({
  value,
  onChange,
}: {
  value: SkinCategory | null;
  onChange: (v: SkinCategory | null) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {CATEGORY_OPTIONS.map((opt) => {
        const selected = opt.key === value;
        return (
          <button
            key={opt.key ?? "any"}
            type="button"
            onClick={() => onChange(opt.key)}
            className={`rounded-xl px-3 py-1.5 text-sm transition ${
              selected
                ? "bg-button text-button-text"
                : "bg-bg-secondary text-text"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
